import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { useReasons, useVendors, usePlants } from "../../lib/useReference";
import { useToast } from "../../lib/toast";
import { formatDate } from "../../lib/dateFormat";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FieldSelect, FieldInput, FieldReadonly, DateFormatRadios } from "../../components/ui/Field";
import { LineStatusBadge } from "../../components/ui/StatusBadge";

interface PoLine {
  id: number;
  poNumber: string;
  poLineItem: number;
  materialNumber: string;
  materialDescription: string;
  deliveryDate: string;
  etdDate: string | null;
  originalQty: number;
  unit: string;
  plantId: number;
  unitPrice: number | null;
  currency: string;
  vendorId: number;
  paymentTerms: string | null;
  mrpControllerNumber: string | null;
  mrpControllerName: string | null;
  regionalBuyer: string | null;
  regionalBuyerName: string | null;
  purchaseOrderGroup: string | null;
  status: "unconfirmed" | "confirmed";
  createdAt: string;
}

interface Comment {
  id: number;
  comment: string;
  createdAt: string;
  createdByEmail: string | null;
}

/**
 * PO Confirmation Split — Submit. Server enforces the documented quantity-
 * mismatch rule: entered quantities must sum to the original line quantity.
 */
export function PoSplit() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: reasons } = useReasons();
  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();
  const [ids] = useState<number[]>(() => JSON.parse(sessionStorage.getItem("po-split-ids") ?? "[]"));
  const [dateFormat, setDateFormat] = useState("DD/MMM/YYYY");
  const [proposals, setProposals] = useState<Record<number, { qty: string; date: string; reasonId: string; comment: string }>>({});

  const vendorMap = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v.vendorNumber])), [vendors]);
  const plantMap = useMemo(() => new Map((plants ?? []).map((p) => [p.id, p.plantNumber])), [plants]);

  const { data } = useQuery({
    queryKey: ["po-lines-detail", ids],
    queryFn: async () => {
      const results = await Promise.all(ids.map((id) => api.get<{ line: PoLine; comments: Comment[] }>(`/po/lines/${id}`)));
      return results.map((r) => ({ ...r.line, comments: r.comments }));
    },
    enabled: ids.length > 0,
  });

  useEffect(() => {
    if (!data) return;
    setProposals((prev) => {
      const next = { ...prev };
      for (const line of data) if (!next[line.id]) next[line.id] = { qty: String(line.originalQty), date: line.deliveryDate, reasonId: "", comment: "" };
      return next;
    });
  }, [data]);

  const submitMutation = useMutation({
    mutationFn: () =>
      api.post("/po/lines/split", {
        lines: (data ?? []).map((line) => ({
          poLineId: line.id,
          proposedQty: Number(proposals[line.id]?.qty ?? line.originalQty),
          proposedDeliveryDate: proposals[line.id]?.date ?? line.deliveryDate,
          reasonId: Number(proposals[line.id]?.reasonId || 1),
          comment: proposals[line.id]?.comment || undefined,
        })),
      }),
    onSuccess: () => {
      toast("Supplier proposals submitted successfully");
      sessionStorage.removeItem("po-split-ids");
      navigate("/po-result");
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : "Submission failed", "error"),
  });

  if (!ids.length) {
    return <Card className="p-8 text-center text-slate-400">No PO lines selected. Go back to PO Confirmation and choose lines to change.</Card>;
  }

  return (
    <Card>
      <div className="p-4 border-b border-slate-100 dark:border-white/10">
        <DateFormatRadios value={dateFormat} onChange={setDateFormat} />
      </div>
      <div className="p-4 space-y-4">
        {data?.map((line) => (
          <div key={line.id} className="border border-slate-200 dark:border-white/10 rounded-lg p-4 space-y-4">
            <div className="text-sm font-semibold">
              {line.poNumber}-{line.poLineItem} · {line.materialNumber} — {line.materialDescription}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <FieldReadonly label="Creation Date" value={formatDate(line.createdAt, dateFormat)} />
              <FieldReadonly label="Line Status" value={<LineStatusBadge status={line.status} />} />
              <FieldReadonly label="Delivery Date" value={formatDate(line.deliveryDate, dateFormat)} />
              <FieldReadonly label="ETD Date" value={formatDate(line.etdDate, dateFormat)} />
              <FieldReadonly label="Plant" value={plantMap.get(line.plantId) ?? line.plantId} />
              <FieldReadonly label="Unit Price" value={line.unitPrice != null ? `${line.currency} ${line.unitPrice}` : "—"} />
              <FieldReadonly label="Vendor" value={vendorMap.get(line.vendorId) ?? line.vendorId} />
              <FieldReadonly label="Payment Terms" value={line.paymentTerms ?? "—"} />
              <FieldReadonly label="MRP Controller" value={line.mrpControllerNumber ? `${line.mrpControllerNumber} — ${line.mrpControllerName ?? ""}` : "—"} />
              <FieldReadonly label="Regional Buyer" value={line.regionalBuyer ? `${line.regionalBuyer} — ${line.regionalBuyerName ?? ""}` : "—"} />
              <FieldReadonly label="Purchase Order Group" value={line.purchaseOrderGroup ?? "—"} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <FieldReadonly label="Original Qty" value={`${line.originalQty} ${line.unit}`} />
              <FieldInput
                label="Proposed Qty"
                value={proposals[line.id]?.qty ?? ""}
                onChange={(e) => setProposals((p) => ({ ...p, [line.id]: { ...p[line.id]!, qty: e.target.value } }))}
              />
              <FieldInput
                type="date"
                label="Proposed Delivery Date"
                value={proposals[line.id]?.date ?? ""}
                onChange={(e) => setProposals((p) => ({ ...p, [line.id]: { ...p[line.id]!, date: e.target.value } }))}
              />
              <FieldSelect
                label="Reason for Change"
                value={proposals[line.id]?.reasonId ?? ""}
                onChange={(e) => setProposals((p) => ({ ...p, [line.id]: { ...p[line.id]!, reasonId: e.target.value } }))}
                options={[{ value: "", label: "Select reason…" }, ...(reasons ?? []).map((r) => ({ value: r.id, label: r.label }))]}
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Add Comment</label>
              <textarea
                className="mt-1 w-full text-sm border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 bg-white dark:bg-white/5 outline-none focus:ring-2 focus:ring-sbd-yellow/50"
                rows={2}
                value={proposals[line.id]?.comment ?? ""}
                onChange={(e) => setProposals((p) => ({ ...p, [line.id]: { ...p[line.id]!, comment: e.target.value } }))}
              />
            </div>

            <div>
              <div className="text-[11px] font-semibold text-slate-500 mb-1.5">Comments</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {line.comments.length ? (
                  line.comments.map((c) => (
                    <div key={c.id} className="text-xs bg-slate-50 dark:bg-white/5 rounded-lg p-2">
                      <div>{c.comment}</div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {c.createdByEmail} · {new Date(c.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-slate-400">No comments on this line yet.</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 flex justify-end gap-2 border-t border-slate-100 dark:border-white/10">
        <Button variant="outline" onClick={() => navigate("/po-result")}>
          Back
        </Button>
        <Button icon={Send} onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
          Submit
        </Button>
      </div>
    </Card>
  );
}
